import { useField, useFormikContext } from "formik";
import { useState, useRef, useEffect } from "react";

const StudentSelectInput = ({
  label,
  data = [],
  onChange,
  labelClassName,
  onSearch,
  showLabel = true,
  ...props
}) => {
  const [field, meta] = useField(props);
  const { values, setFieldValue } = useFormikContext(); // Access full Formik values
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredData, setFilteredData] = useState(data);
  const [isTyping, setIsTyping] = useState(false);
  const dropdownRef = useRef(null);
  const isError = meta.touched && meta.error;

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Debounced search
  useEffect(() => {
    if (!isOpen || !onSearch) return;
    const trimmed = searchTerm.trim();
    if (trimmed === "") return;

    setIsTyping(true);
    const timer = setTimeout(() => {
      onSearch(trimmed);
      setIsTyping(false);
    }, 600);

    return () => clearTimeout(timer);
  }, [searchTerm, isOpen, onSearch]);

  // Local filter
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredData(data);
    } else {
      setFilteredData(
        data.filter(item =>
          item.cityName?.toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }
  }, [data, searchTerm]);

  // NO NEED for useEffect to sync name — derive directly from Formik values
  const displayName = values?.[props.name]?.name || label;

  const handleSelect = (item) => {
    // Update Formik with object {id, name} — this triggers re-render
    setFieldValue(props.name, {
      id: item.cityId,
      name: item.cityName
    });

    if (onChange) onChange(item); // optional
    setIsOpen(false);
    setSearchTerm("");
  };

  return (
    <div className="flex flex-col">
      {showLabel && (
        <label
          className={`p-1 mt-2 text-gray-700 flex gap-1 ${labelClassName || 'text-lg'}`}
          htmlFor={props.id || props.name}
        >
          {label}
          {isError && <span className="text-red-500 text-[12px]">*</span>}
        </label>
      )}

      <div className="relative" ref={dropdownRef}>
        <div
          className={`p-2 text-gray-700 border rounded-lg cursor-pointer bg-white ${
            isError ? "border-red-500" : "border-gray-200"
          }`}
          onClick={() => setIsOpen(!isOpen)}
        >
          <span className="block w-full text-gray-600 text-[14px] truncate">
            {displayName}
          </span>
        </div>

        {isOpen && (
          <div className="absolute z-50 mt-1 w-full bg-white rounded-lg shadow-lg max-h-56 overflow-y-auto">
            <div className="sticky top-0 bg-white z-10 border-b border-gray-200">
              <input
                type="text"
                autoFocus
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-200 placeholder-gray-400 placeholder:text-[14px]"
              />
            </div>

            {filteredData.length > 0 ? (
              filteredData.map((item, idx) => (
                <div
                  key={idx}
                  className="px-4 py-2 hover:bg-orange-100 cursor-pointer text-sm"
                  onClick={() => handleSelect(item)}
                >
                  {item.cityName}
                </div>
              ))
            ) : (
              <div className="px-3 py-2 text-gray-500 text-sm">
                No results found
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentSelectInput;